import {
  STATE_LOADING,
  STATE_ERROR,
  STATE_LOADED,
  stateType,
} from './../state';
import {
  flow,
  type Instance,
  types,
  type IAnyModelType,
  cast,
} from 'mobx-state-tree';
import { set } from 'mobx';
import type { Locale as MelwaysLocale } from '@seek/melways-sites';

import Job, { type IJob } from './Job';
import { logError } from '../../utils/logError';
import { AccessType } from './types';
import QuestionnaireStore from './QuestionnaireStore';
import { fetchHirerGraphJobInfo } from 'src/graphql/hirer/fetchHirerGraphJobInfo';
import type { JobInfoQuery } from 'src/App/AppPageBlock/graphql/hirer-types.generated';

const JobStore = types
  .model('JobStore', {
    job: types.optional(Job, {}),
    state: stateType,
    accessType: types.optional(
      types.enumeration<AccessType>('AccessType', Object.values(AccessType)),
      AccessType.AUTHORISED,
    ),
    questionnaire: types.optional(
      types.late((): IAnyModelType => QuestionnaireStore),
      {
        questionnaire: {
          state: STATE_LOADING,
        },
      },
    ),
  })
  .views((self) => ({
    get isError() {
      return self.state === STATE_ERROR;
    },

    get isLoading() {
      return self.state === STATE_LOADING;
    },

    get hasAccess(): boolean {
      return self.accessType === AccessType.AUTHORISED;
    },
  }))
  .actions((self) => {
    const loadHirerJobDetails = flow(function* (
      jobId: string,
      seekAdvertiserId: string,
      language: MelwaysLocale,
    ) {
      try {
        const data: JobInfoQuery = yield fetchHirerGraphJobInfo(
          seekAdvertiserId,
          jobId,
          language,
        );

        const jobDetails = data?.getJobDetails;

        if (jobDetails) {
          self.job.jobInformation.analyticsAdRatingStatus =
            jobDetails.performanceRating?.status ?? null;
          self.job.jobInformation.setPostChannel(jobDetails.postChannel);
          self.job.jobInformation.status = jobDetails.status;
          self.job.jobInformation.adType = jobDetails.adType ?? null;
          self.job.jobInformation.productDisplayName =
            jobDetails.productDisplayName || null;
          self.job.jobInformation.title = jobDetails.title;
          self.job.jobInformation.workType = jobDetails.workType ?? null;
          self.job.jobInformation.locations = cast(
            jobDetails.locations.map((location) => location.description),
          );
          self.job.jobInformation.classification =
            jobDetails.categoryName ?? null;
          self.job.jobInformation.subClassification =
            jobDetails.subCategoryName ?? null;
          self.job.jobInformation.internalReference =
            jobDetails.advertiserJobReference ?? undefined;

          self.job.jobInformation.salary = jobDetails.salaryText ?? null;
          self.job.jobInformation.salaryMin = jobDetails.minSalary ?? null;
          self.job.jobInformation.salaryMax = jobDetails.maxSalary ?? null;
          self.job.jobInformation.salaryCurrency =
            jobDetails.salaryCurrency ?? null;
          self.job.jobInformation.setSalaryType(jobDetails.payType || null);

          if (
            jobDetails.billableApplicants.__typename ===
            'BillableApplicantsResponseSuccess'
          ) {
            self.job.jobInformation.billableApplicants = jobDetails
              .billableApplicants.data
              ? {
                  success: true,
                  count: jobDetails.billableApplicants.data.count,
                }
              : undefined;
          } else if (
            jobDetails.billableApplicants.__typename === 'ResponseError'
          ) {
            self.job.jobInformation.billableApplicants = {
              success: false,
            };
          }
        }

        self.state = STATE_LOADED;
      } catch (error: any) {
        logError(
          {
            errorCode: 'GET_JOB_DETAILS',
            description: 'Error attempting: fetchHirerGraphJobInfo',
          },
          { error, seekAdvertiserId, jobId, language },
        );
        self.state = STATE_ERROR;
      }
    });

    const setAccessType = (accessType?: AccessType) => {
      if (accessType) {
        self.accessType = accessType;
      }
    };

    const setJob = (job: IJob) => {
      set(self.job, job);
      self.state = STATE_LOADED;
    };

    const setIsError = () => {
      self.state = STATE_ERROR;
    };

    return {
      loadHirerJobDetails,
      setAccessType,
      setJob,
      setIsError,
    };
  });

export interface IJobStore extends Instance<typeof JobStore> {}

export default JobStore;
